import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { MapPin, Users, Globe, Linkedin } from "lucide-react";
import { useTranslation } from "react-i18next";
import OfficeMap from "@/components/OfficeMap";
import aboutHero from "@/assets/about-hero.webp";
import ethanCutout from "@/assets/ethan-cutout.png";
import teamEthan from "@/assets/team-ethan.jpg";
import teamAndreas from "@/assets/andreas.png";
import teamKen from "@/assets/team-ken.png";
import teamUlf from "@/assets/team-ulf.png";

const teamPhotos = [
  { key: "ethan", name: "Ethan Faghani", photo: teamEthan, linkedin: "https://www.linkedin.com/in/ethanfaghani/" },
  { key: "andreas", name: "Andreas Andersson", photo: teamAndreas, linkedin: "https://www.linkedin.com/in/andreas1andersson/" },
  { key: "ken", name: "Ken Ryrbo", photo: teamKen, linkedin: "https://www.linkedin.com/in/ryrbo/" },
  { key: "ulf", name: "Ulf Holmberg", photo: teamUlf, linkedin: "https://www.linkedin.com/in/ulf-holmberg-3a3525b/" },
];

const ease = [0.16, 1, 0.3, 1] as const;

const AboutUs = () => {
  const { t, i18n } = useTranslation('about');

  const milestones = [
    { year: "2020", title: t('journey.2020') },
    { year: "2021", title: t('journey.2021') },
    { year: "2022", title: t('journey.2022') },
    { year: "2023", title: t('journey.2023') },
    { year: "2024", title: t('journey.2024') },
    { year: "2025", title: t('journey.2025') },
  ];

  const goals = [
    { number: "11", title: t('sustainability.goal11Title'), desc: t('sustainability.goal11Desc'), bg: "bg-[hsl(33,90%,55%)]" },
    { number: "13", title: t('sustainability.goal13Title'), desc: t('sustainability.goal13Desc'), bg: "bg-[hsl(145,45%,40%)]" },
    { number: "14", title: t('sustainability.goal14Title'), desc: t('sustainability.goal14Desc'), bg: "bg-[hsl(205,75%,50%)]" },
  ];

  return (
    <div className="min-h-screen section-fade">
      <Helmet>
        <html lang={i18n.language} />
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
        <link rel="canonical" href="https://www.cetasol.com/about-us" />
        <meta property="og:title" content={t('meta.title')} />
        <meta property="og:description" content={t('meta.description')} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cetasol.com/about-us" />
        <meta property="og:image" content="https://www.cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('meta.title')} />
        <meta name="twitter:description" content={t('meta.description')} />
        <meta name="twitter:image" content="https://www.cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "About Us", "item": "https://www.cetasol.com/about-us" }
          ]
        })}</script>
      </Helmet>
      <Header />
      <main className="pb-20">
        {/* Hero */}
        <section className="relative pt-24">
          <div className="absolute inset-0">
            <img src={aboutHero} alt="Cetasol team reviewing maritime data at harbor" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-[hsl(212,60%,10%)]/80" />
          </div>
          <div className="relative container mx-auto px-6 py-24 md:py-32">
            <div className="max-w-2xl">
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
                <p className="text-xs font-semibold tracking-widest uppercase text-primary mb-4">{t('hero.label')}</p>
                <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-8 leading-[1.1]">{t('hero.heading')}</h1>
                <p className="text-lg text-white/80 leading-relaxed mb-6">{t('hero.p1')}</p>
                <p className="text-lg text-white/80 leading-relaxed">{t('hero.p2')}</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="bg-surface-light py-20">
          <div className="container mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease }} className="grid grid-cols-1 md:grid-cols-3 gap-12 md:divide-x md:divide-border">
              <div>
                <Users className="text-foreground mb-3" size={28} />
                <h3 className="font-display text-foreground mb-2"><span className="text-4xl md:text-5xl font-bold block">{t('stats.peopleValue')}</span> <span className="text-lg font-semibold">{t('stats.peopleLabel')}</span></h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t('stats.peopleDesc')}</p>
              </div>
              <div className="md:pl-12">
                <MapPin className="text-foreground mb-3" size={28} />
                <h3 className="font-display text-foreground mb-2"><span className="text-4xl md:text-5xl font-bold block">{t('stats.presenceValue')}</span> <span className="text-lg font-semibold">{t('stats.presenceLabel')}</span></h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t('stats.presenceDesc')}</p>
              </div>
              <div className="md:pl-12">
                <Globe className="text-foreground mb-3" size={28} />
                <h3 className="font-display text-foreground mb-2"><span className="text-4xl md:text-5xl font-bold block">{t('stats.vesselsValue')}</span> <span className="text-lg font-semibold">{t('stats.vesselsLabel')}</span></h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t('stats.vesselsDesc')}</p>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Timeline */}
        <section className="section-fade py-[120px]">
          <div className="container mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease }}>
              <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('journey.label')}</p>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-10">{t('journey.heading')}</h2>
              <div className="relative">
                <div className="absolute top-3 left-0 right-0 h-[2px] bg-gradient-to-r from-primary/40 via-primary/20 to-primary/40" />
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
                  {milestones.map((milestone, i) => (
                    <motion.div key={milestone.year} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }} className="relative pt-8 text-center">
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[7px] h-[7px] rounded-full bg-primary" />
                      <p className="font-display text-sm font-bold text-primary mb-1">{milestone.year}</p>
                      <h3 className="font-display text-xs md:text-sm font-normal text-foreground">{milestone.title}</h3>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Vision */}
        <section className="bg-surface-light py-20">
          <div className="container mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease }} className="max-w-4xl mx-auto">
              <div className="rounded-lg pl-0 pr-8 md:pr-12 pt-8 md:pt-12 pb-0 flex flex-col md:flex-row items-end gap-6 md:gap-12 overflow-hidden">
                <img src={ethanCutout} alt="Ethan Faghani, CEO & Founder of Cetasol" className="w-44 md:w-72 flex-shrink-0 object-contain self-end ml-6 md:ml-12" />
                <div className="pb-8 md:pb-12 px-4 md:px-0">
                  <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('vision.label')}</p>
                  <h2 className="font-display text-lg md:text-2xl font-bold text-foreground mb-4 leading-snug">{t('vision.quote')}</h2>
                  <p className="text-sm text-muted-foreground">{t('vision.attribution')}</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Sustainability */}
        <section className="section-fade py-[120px]">
          <div className="container mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease }}>
              <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">{t('sustainability.label')}</p>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-6">{t('sustainability.heading')}</h2>
              <p className="text-muted-foreground leading-relaxed max-w-2xl mb-10">{t('sustainability.description')}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {goals.map((goal, i) => (
                  <motion.div key={goal.number} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }} className={`${goal.bg} rounded-lg p-6 text-white`}>
                    <span className="font-display text-4xl font-bold opacity-90">{goal.number}</span>
                    <h3 className="font-display text-lg font-semibold mt-2 mb-3">{goal.title}</h3>
                    <p className="text-sm leading-relaxed text-white/85">{goal.desc}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Leadership */}
        <section className="bg-surface-light py-20">
          <div className="container mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease }}>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-10">{t('leadership.heading')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {teamPhotos.map((person, i) => (
                  <motion.div key={person.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.08 }} className="bg-[hsl(216,25%,16%)] rounded-lg p-6 border border-border text-foreground">
                    <img src={person.photo} alt={person.name} className="w-20 h-20 rounded-full object-cover object-[center_30%] bg-muted mb-4" />
                    <h3 className="font-display text-lg font-semibold text-white">{t(`leadership.${person.key}Name`, { defaultValue: person.name })}</h3>
                    <p className="text-sm text-primary font-medium mb-3">{t(`leadership.${person.key}Title`)}</p>
                    <p className="text-sm text-white/70 leading-relaxed mb-3">{t(`leadership.${person.key}Bio`)}</p>
                    <a href={person.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors font-medium"><Linkedin size={16} /> LinkedIn</a>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Office Map */}
        <section className="py-20 section-fade">
          <div className="container mx-auto px-6">
            <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.6, ease }}>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-10">{t('offices.heading')}</h2>
              <OfficeMap />
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutUs;
